// src/pages/projects/ProjectBudget.jsx - Refactored
import React from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import useAuthStore from '../../stores/authStore';
import useConfirmDialog from '../../hooks/useConfirmationDialog';
import { EmptyState } from '../../design-system/components/core';
import DetailPageLayout from '../../components/layout/DetailPageLayout';

// Custom hooks and utils
import useProjectBudget from './hooks/useProjectBudget';
import { calculateBudgetSummary } from '../../utils/budgetUtils';

// Components
import BudgetSummary from './components/BudgetSummary';
import TransactionsTable from './components/TransactionsTable';
import AddTransactionDialog from './components/AddTransactionDialog';

/**
 * Project Budget page component - Refactored
 */
const ProjectBudget = () => {
  const { id } = useParams();
  
  // Get project from outlet context instead of fetching it again
  const { project, canEdit } = useOutletContext();
  
  // Custom hooks
  const {
    transaction,
    setTransaction,
    isAddTransactionOpen,
    setIsAddTransactionOpen,
    handleAddTransaction,
    handleDeleteTransaction
  } = useProjectBudget(id);
  
  // Confirm dialog
  const [ConfirmDialogComponent, confirmDelete] = useConfirmDialog();
  
  // Get budget state from project store
  const { isLoading, error } = useAuthStore();
  
  // Calculate budget summary
  const { totalBudget, totalExpenses, totalIncome, remaining, transactions } = 
    project ? calculateBudgetSummary(project) : 
    { totalBudget: 0, totalExpenses: 0, totalIncome: 0, remaining: 0, transactions: [] };
  
  // Action buttons
  const actionButtons = canEdit ? [
    {
      label: 'Add Transaction',
      icon: <AddIcon />,
      onClick: () => setIsAddTransactionOpen(true),
      variant: 'contained'
    }
  ] : [];
  
  return (
    <>
      <DetailPageLayout
        title="Project Budget"
        breadcrumbs={[
          { label: 'Projects', to: '/projects' },
          { label: project?.name, to: `/projects/${id}` },
          { label: 'Budget' }
        ]}
        actionButtons={actionButtons}
        isLoading={isLoading}
        error={error}
        isEmpty={!project}
        emptyStateComponent={
          <EmptyState
            title="Project not found"
            description="The project you're looking for doesn't exist or you don't have access to it."
            action={
              <Button 
                variant="contained" 
                component={Link}
                to="/projects"
              >
                Back to Projects
              </Button>
            }
          />
        }
      >
        {/* Budget Summary */}
        <BudgetSummary
          totalBudget={totalBudget}
          totalExpenses={totalExpenses}
          totalIncome={totalIncome}
          remaining={remaining}
        />
        
        {/* Transactions Table */}
        <TransactionsTable
          transactions={transactions}
          canEdit={canEdit}
          onAddTransaction={() => setIsAddTransactionOpen(true)}
          onDeleteTransaction={(transaction) => handleDeleteTransaction(transaction, confirmDelete)}
        />
      </DetailPageLayout>
      
      {/* Add Transaction Dialog */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AddTransactionDialog
          open={isAddTransactionOpen}
          onClose={() => setIsAddTransactionOpen(false)}
          transaction={transaction}
          setTransaction={setTransaction}
          onAddTransaction={handleAddTransaction}
        />
      </LocalizationProvider>
      
      {/* Confirm Dialog */}
      <ConfirmDialogComponent />
    </>
  );
};

export default ProjectBudget;